/**
 * Место коэффициента прямо в формуле.
 *
 * Поле стоит там, где в записи стоит буква: `y = [2] x + [−4]`. Отдельная
 * форма под графиком заставляла держать в голове, какая строка чему
 * соответствует, — здесь редактируется сама формула.
 *
 * Ширина поля идёт за содержимым: фиксированная колонка либо режет «−12,5»,
 * либо оставляет пустоту вокруг «1» и рвёт формулу на части.
 */

import { useEffect, useRef, useState } from "react";
import { equalsExact, formatExact, parseExact, type ExactNumber } from "../../lib/function-lab/numbers";

/**
 * Показ значения. Минус — типографский, как во всём остальном тексте;
 * `parseExact` принимает его наравне с дефисом, поэтому набранное учеником
 * `-2` разбирается по-прежнему.
 */
function show(value: ExactNumber): string {
  return formatExact(value).replace(/^-/, "−");
}

export function ExactField({ value, label, onSettle }: {
  value: ExactNumber;
  /** Доступное имя: «наклон прямой y = 2x», а не просто «a». */
  label: string;
  onSettle: (next: ExactNumber) => void;
}) {
  const [draft, setDraft] = useState(() => show(value));
  const [error, setError] = useState<string | null>(null);
  const editing = useRef(false);

  useEffect(() => {
    if (!editing.current) { setDraft(show(value)); setError(null); }
  }, [value]);

  const submit = (text: string) => {
    const outcome = parseExact(text);
    if (!outcome.ok) { setError(outcome.error); return; }
    setError(null);
    // `-2` и `−2` — одно значение, набранное по-разному. Записывать такой
    // уход из поля шагом значит тратить отмену на переход по Tab.
    if (equalsExact(outcome.value, value)) { setDraft(show(outcome.value)); return; }
    onSettle(outcome.value);
  };

  return <span className="fn-lab__slot">
    <input className={`fn-lab__slot-input${error ? " fn-lab__slot-input--bad" : ""}`}
      value={draft} size={Math.max(2, draft.length)} inputMode="decimal" autoComplete="off"
      aria-label={label} aria-invalid={error !== null}
      onFocus={() => { editing.current = true; }}
      onChange={(event) => setDraft(event.target.value)}
      onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); submit(draft); } }}
      onBlur={() => {
        editing.current = false;
        if (draft === show(value)) { setError(null); return; }
        submit(draft);
      }} />
    {error ? <span className="fn-lab__slot-error" role="alert">{error}</span> : null}
  </span>;
}
